import { useEffect, useState } from 'react';

import { AddItem, Empty, Skeleton } from '@components/index';
import { Container, Header, MovieTMDBSearch, Subheader } from '@elements/index';
import { MovieTMDBSearchType, MovieType } from '@elements/Movie/types';
import { useMutationDeleteSchedule, useMutationEditMovie, useQueryMoviesGetBy, useQueryMovieTMDB } from '@hooks/index';
import useMutationDeleteMovie from '@hooks/movies/useMutationDeleteMovie';
import useQueryMovie from '@hooks/movies/useQueryMovie';
import { IconMovieCards } from '@icons/index';
import { IMovieScheduleDto, IMovieTMDB, MovieUpdateDto } from '@interfaces/movieInterface';
import { Button, Popconfirm } from 'antd';
import { useFormik } from 'formik';
import { PlusCircle, Trash2 } from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useToast } from '@components/Toast/ToastProvider';

import MovieScheduleItem from './MovieScheduleItem';

type FieldName = keyof IMovieScheduleDto;

const MovieEdit = () => {
	const { movieId } = useParams();
	const navigate = useNavigate();

	const [title, setTitle] = useState('');
	const [year, setYear] = useState('');

	const [selectMovieTMDB, setSelectMovieTMDB] = useState<IMovieTMDB>();

	const { data: movie, isFetching: isLoadingMovie } = useQueryMovie(Number(movieId));
	const { data: movieTMDB, isFetched: isFetchedMovieTMDB } = useQueryMovieTMDB({
		movieTMDBId: movie?.movieDBId as number,
		config: { enabled: !!movie?.movieDBId && movie.movieDBId !== null }
	});

	const { mutateAsync: mutateMovie, status: _mutationMovieStatus } = useMutationEditMovie();
	const { mutateAsync: mutateDeleteScheduleAsync } = useMutationDeleteSchedule();
	const { mutateAsync: mutateDeleteMovie, status: _statusDeleteMovie } = useMutationDeleteMovie();
	const { data: movieInDatabase } = useQueryMoviesGetBy(selectMovieTMDB?.id as number);

	const { showSuccess, showError } = useToast();

	const onSubmitHandler = async (values: MovieUpdateDto) => {
		try {
			await mutateMovie(values);
			showSuccess({ description: 'Le film a été correctement modifié.' });
			navigate('/movies');
		} catch {
			showError({ description: 'Une erreur est survenue, veuillez réessayer plus tard.' });
		}
	};

	// Form
	const { values, setValues, isValid, dirty, handleSubmit } = useFormik<MovieUpdateDto>({
		initialValues: {
			id: 0,
			title: '',
			movieDBId: null,
			poster_path: '',
			schedules: [{ date: '', type: [] }]
		},
		onSubmit: onSubmitHandler,
		validationSchema: Yup.object({
			title: Yup.string().required('El titulo es requerido'),
			schedules: Yup.array().of(
				Yup.object({
					date: Yup.string().required('La fecha y hora es requerida'),
					type: Yup.array().min(1, 'Minimo un tipo debe ser seleccionado')
				})
			)
		})
	});

	const handlerMovieSelected = (movieTMDBSelected: IMovieTMDB): void => {
		setValues((prevValues) => ({
			...prevValues,
			movieDBId: movieTMDBSelected.id ? Number(movieTMDBSelected.id) : null,
			poster_path: movieTMDBSelected.poster_path
		}));
	};

	const updateScheduleField = <T extends FieldName>(
		fieldName: T,
		value: IMovieScheduleDto[T],
		index: number
	): void => {
		setValues((prevValues) => {
			const { schedules } = prevValues;
			const newSchedules = [...schedules];
			const item = { ...newSchedules[index] };
			item[fieldName] = value;
			newSchedules[index] = item;
			return { ...prevValues, schedules: newSchedules };
		});
	};

	const addScheduleItem = () => {
		setValues((prevValues) => ({
			...prevValues,
			schedules: [...prevValues.schedules, { date: '', type: [] }]
		}));
	};

	const onDeleteScheduleItem = async (index: number, scheduleId: number) => {
		setValues((prevValues) => {
			const { schedules } = prevValues;
			const newSchedules = schedules.filter((_, i) => i !== index);
			return { ...prevValues, schedules: newSchedules };
		});

		try {
			await mutateDeleteScheduleAsync({ scheduleId });
			showSuccess({ description: 'La séance a été correctement supprimée.' });
		} catch {
			showError({ description: 'Une erreur est survenue lors de la suppression.' });
		}
	};

	const onDeleteMovie = async (movieToDelte: number) => {
		try {
			await mutateDeleteMovie({ movieId: movieToDelte });
			showSuccess({ description: 'Le film a été correctement supprimé.' });
			navigate('/movies');
		} catch {
			showError({ description: 'Une erreur est survenue, veuillez réessayer plus tard.' });
		}
	};

	useEffect(() => {
		if (!movie) return;

		setTitle(movie.title);
		setValues({
			id: movie.id,
			title: movie.title,
			movieDBId: movie.movieDBId,
			poster_path: movie.poster_path,
			schedules: movie.schedules
		});

		if (movieTMDB && Number(movie.movieDBId) === movieTMDB.id) {
			setSelectMovieTMDB(movieTMDB);
		} else {
			setSelectMovieTMDB(undefined);
		}
	}, [movie, movieTMDB, setValues]);

	const handleTitleChange = (newTitle: string) => {
		setTitle(newTitle);
		setValues((prev) => ({ ...prev, title: newTitle }));
	};

	return (
		<>
			<Header />
			<Container>
				<Subheader title="Editer un film" />

				<div className="flex flex-col gap-2 md:grid md:grid-cols-5">
					<div className="md:col-span-2">
						{isLoadingMovie && (
							<div className="flex flex-col gap-2 rounded-lg bg-white p-4">
								<Skeleton height={28} width={120} />
								<Skeleton height={40} width="100%" />
								<Skeleton height={40} width="100%" />
								<Skeleton height={80} width="100%" />
								<Skeleton height={40} width="100%" />
							</div>
						)}
						{!isLoadingMovie && (
							<>
								<h3 className="mb-2 text-lg font-bold text-slate-600">Information du film</h3>
								<div className="flex flex-col gap-2">
									<MovieTMDBSearch
										type={MovieType.Movie}
										movieInDatabase={movieInDatabase}
										mode={MovieTMDBSearchType.Edit}
										title={title as string}
										year={year as string}
										setTitle={handleTitleChange}
										setYear={setYear}
										selectMovieTMDB={selectMovieTMDB as IMovieTMDB}
										setSelectMovieTMDB={setSelectMovieTMDB}
										handlerMovieSelected={handlerMovieSelected}
										isLoading={isFetchedMovieTMDB || isLoadingMovie}
									/>
									{movie && (
										<Popconfirm
											placement="bottom"
											title="Supprimer le film"
											description="Souhaitez-vous supprimer ce film ?"
											onConfirm={() => onDeleteMovie(movie.id)}
											okText="Oui, supprimer"
											cancelText="Annuler"
										>
											<Button className="" block type="primary" danger size="large" htmlType="button">
												Supprimer le film
											</Button>
										</Popconfirm>
									)}
								</div>
							</>
						)}
					</div>
					<div className="col-span-3">
						<h3 className="mb-2 text-lg font-bold text-slate-600">Ajouter une séance</h3>
						<div className="">
							<form noValidate autoComplete="off" onSubmit={handleSubmit}>
								<div className="flex flex-col gap-2">
									{!isLoadingMovie &&
										movie &&
										values.schedules &&
										values.schedules.length > 0 &&
										values.schedules.map((schedule, key) => (
											<MovieScheduleItem
												// eslint-disable-next-line react/no-array-index-key
												key={key}
												index={key}
												controls={
													<Popconfirm
														placement="left"
														title="Supprimer la séance"
														description="Souhaitez-vous éliminer la séance?"
														onConfirm={() => onDeleteScheduleItem(key, schedule.id as number)}
														okText="Oui, supprimer"
														cancelText="Annuler"
													>
														<button className="text-red-700" type="button">
															<Trash2 size={17} />
														</button>
													</Popconfirm>
												}
												onChangeDateSchedule={(value, index) => {
													if (!value) return;
													updateScheduleField('date', value.toISOString(), index);
												}}
												onChangeTypeSchedule={(value, index) => {
													updateScheduleField('type', value, index);
												}}
												schedule={schedule}
											/>
										))}

									{isLoadingMovie && (
										<>
											<div className="grid h-22 grid-cols-5 gap-2 rounded-lg bg-white p-4">
												<div className="col-span-2 flex flex-col gap-2">
													<Skeleton height={18} width={120} />
													<Skeleton height={27} width="100%" />
												</div>
												<div className="col-span-3 flex flex-col gap-2">
													<Skeleton height={18} width={120} />
													<div className="flex gap-3">
														<Skeleton height={27} width={75} />
														<Skeleton height={27} width={45} />
														<Skeleton height={27} width={76} />
														<Skeleton height={27} width={60} />
														<Skeleton height={27} width={60} />
													</div>
												</div>
											</div>
											<div className="grid h-22 grid-cols-5 gap-2 rounded-lg bg-white p-4">
												<div className="col-span-2 flex flex-col gap-2">
													<Skeleton height={18} width={120} />
													<Skeleton height={27} width="100%" />
												</div>
												<div className="col-span-3 flex flex-col gap-2">
													<Skeleton height={18} width={120} />
													<div className="flex gap-3">
														<Skeleton height={27} width={75} />
														<Skeleton height={27} width={45} />
														<Skeleton height={27} width={76} />
														<Skeleton height={27} width={60} />
														<Skeleton height={27} width={60} />
													</div>
												</div>
											</div>
											<div className="grid h-22 grid-cols-5 gap-2 rounded-lg bg-white p-4">
												<div className="col-span-2 flex flex-col gap-2">
													<Skeleton height={18} width={120} />
													<Skeleton height={27} width="100%" />
												</div>
												<div className="col-span-3 flex flex-col gap-2">
													<Skeleton height={18} width={120} />
													<div className="flex gap-3">
														<Skeleton height={27} width={75} />
														<Skeleton height={27} width={45} />
														<Skeleton height={27} width={76} />
														<Skeleton height={27} width={60} />
														<Skeleton height={27} width={60} />
													</div>
												</div>
											</div>
										</>
									)}

									{values.schedules && values.schedules.length === 0 && (
										<div className="rounded-lg bg-white p-12">
											<Empty
												icon={<IconMovieCards />}
												title="No hay Schedules por el momento."
												description="Por favor agregar Schedules"
											/>
										</div>
									)}

									<AddItem height={88} onClick={() => addScheduleItem()}>
										<PlusCircle size={16} className="mr-1" /> Ajouter une séance
									</AddItem>
								</div>
								<div className="my-2 flex justify-end gap-2">
									<Button type="primary" htmlType="submit" disabled={!(isValid && dirty)}>
										Editer le film
									</Button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</Container>
		</>
	);
};

export default MovieEdit;
